import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MatDialog } from "@angular/material";
import { SelectItem } from "primeng/primeng";

import { BrowserTitleService } from "../common/browser-title/browser-title.service";
import { GenericTable, GenericTableComponent } from "../genericTable";
import { SessionsService } from "./sessions.service";
import { SessionView, SessionStatus } from "./models";
import { TranslateService } from "../common/translate/translate.service";
import { supportEmail, createDateTimeDisplayCallback } from "../util";
import { DeviceType } from "./models";
import { SnackbarService } from "../common/snackbar/snackbar.service";
import { SessionsDisconnectDialogComponent } from "./sessions-disconnect-dialog-component";

interface SessionRouteParams {
	status?: SessionStatus;
}

@Component({
	templateUrl: "./sessions.component.html"
})
export class SessionsComponent implements OnInit {

	@ViewChild(GenericTableComponent, { static: false })
	protected sessionsTable!: GenericTableComponent;

	public readonly refreshIcon = { type: "material", name: "refresh", cssClass: "sdp-icon-button-icon" };
	public readonly supportEmail = supportEmail;

	public sessionsData: SessionView[] | undefined = undefined;
	public columnSchema: GenericTable.ColumnSchema[];
	public errorMessage: string | undefined = undefined;
	public status: SessionStatus | undefined = undefined;

	constructor(
		private service: SessionsService,
		private titleService: BrowserTitleService,
		private translateService: TranslateService,
		private snackbarService: SnackbarService,
		private dialog: MatDialog,
		private route: ActivatedRoute
	) {
		this.columnSchema = this.createTableSchema();
	}

	public async ngOnInit() {
		this.titleService.setTitle("sessions.title");

		this.route.params.subscribe(async (params: SessionRouteParams) => {
			this.status = params.status;
			await this.refresh();
		});
	}

	public async refresh() {
		try {
			if (!!this.status) {
				this.errorMessage = undefined;
				if (!!this.sessionsTable) {
					this.sessionsTable.isDataLoaded = false;
				}
				this.sessionsData = await this.service.getSessions(this.status);

			}
		} catch (e) {
			const message = this.translateService.translate("error.api.generic.message");
			this.errorMessage = message;
		}
	}

	private createTableSchema(): GenericTable.ColumnSchema[] {
		const deviceTypes: SelectItem[] = [];

		for (const property in DeviceType.Type) {
			if (DeviceType.Type.hasOwnProperty(property)) {
				const dt = DeviceType.Type[<DeviceType.BackingType>property];
				const value: SelectItem = { label: dt, value: property };
				deviceTypes.push(value);
			}
		}

		const dateFormat = createDateTimeDisplayCallback(this.translateService.translate("sdp.label.active"));

		return [
			{
				minWidth: "14%",
				type: "dateTime",
				headerSchema: {
					type: "dateTime",
					sortable: true,
					dateFormat: "mm/dd/yy",
					hourFormat: "12",
					headerText: "session.startTime",
					filterDirection: "gt"
				},
				bodySchema: {
					type: "dateTime",
					boundField: "startTime",
					displayCallback: dateFormat
				}
			}, {
				minWidth: "14%",
				type: "dateTime",
				headerSchema: {
					type: "dateTime",
					sortable: true,
					dateFormat: "mm/dd/yy",
					hourFormat: "12",
					headerText: "session.endTime",
					filterDirection: "lt"
				},
				bodySchema: {
					type: "dateTime",
					boundField: "endTime",
					displayCallback: dateFormat
				}
			}, {
				minWidth: "17%",
				type: "string",
				headerSchema: {
					type: "substring",
					sortable: true,
					headerText: "session.userName"
				},
				bodySchema: {
					type: "string",
					boundField: "userName"
				}
			}, {
				minWidth: "17%",
				type: "string",
				headerSchema: {
					type: "substring",
					sortable: true,
					headerText: "session.deviceName"
				},
				bodySchema: {
					type: "string",
					boundField: "deviceName"
				}
			}, {
				minWidth: "17%",
				type: "string",
				headerSchema: {
					type: "substring",
					sortable: true,
					headerText: "session.gatewayType"
				},
				bodySchema: {
					type: "string",
					boundField: "gatewayName",
					cssClass: (row: SessionView) => {
						if (row.gatewayName === "Gateway Removed") {
							return "sdp-gateway-removed";
						} else {
							return "";
						}
					}
				}
			}, {
				minWidth: "15%",
				type: "deviceType",
				headerSchema: {
					type: "multiSelect",
					choices: deviceTypes,
					headerText: "session.deviceType",
					sortable: true
				},
				bodySchema: {
					type: "deviceType",
					boundField: "type"
				}
			}, {
				minWidth: "6%",
				type: "button",
				headerSchema: {
					type: "none",
				},
				bodySchema: {
					type: "button",
					buttons: [
						{
							type: "icon",
							icon: { type: "fa", name: "chain-broken", cssClass: "sdp-icon-button-icon" },
							onClick: (row: SessionView) => {
								this.disconnectDialog(row)
									.catch((e) => {
										this.snackbarService.open(this.translateService.translate(e));
									});
							},
							tooltip: () => {
								return "sessions.disconnect.tooltip.button";
							},
							showButton: (row: SessionView) => {
								// TODO check for userType can go away once all sessions have userType
								// only show the disconnect button for active sessions
								return !row.endTime && !!row.userType;
							}
						}
					]
				}
			}
		];
	}

	private async disconnectDialog(row: SessionView) {
		if (!row.userType) {
			// TODO this case goes away once all session data must contain a userType
			throw new Error("No user type available, unable to disconnect sessions");
		} else {
			const dialogRef = this.dialog.open(SessionsDisconnectDialogComponent);
			dialogRef.componentInstance.session = row;
			// no need to handle closing, all the business logic for saving happens in the dialog component, through the service
		}
	}
}
